import { useEffect } from "react";
import { Navigate, Outlet, useParams } from "react-router-dom";
import styled from "styled-components";
import { useBranchVersions } from "~/features/branch-version/hooks/use-branch-versions";
import { useSelectedVersion } from "~/features/branch-version/hooks/use-selected-version";
import { readVersionIdForBranch } from "~/features/branch-version/lib/selected-version-storage";
import { BranchForbiddenPage } from "~/pages/branch-forbidden/BranchForbiddenPage";
import { isBranchForbiddenError } from "~/shared/api/branch-access";
import { GNB } from "~/widgets/gnb/GNB";
import { WorkflowBar } from "~/widgets/workflow-bar/WorkflowBar";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  min-width: 1280px;
  overflow: hidden;
`;

const Main = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

// URL branchUid 와 SelectedVersion 동기화. branchUid 가 바뀌면 per-branch 저장값 또는
// latest version 으로 자동 채워서 이전 branch 의 stale version 이 새 branch 화면에 흘러들어가지 않게 함.
function useSelectedVersionForCurrentBranch() {
  const { branchUid } = useParams();
  const { selectedVersion, setSelectedVersion } = useSelectedVersion();
  const { data: versions } = useBranchVersions(branchUid);

  useEffect(() => {
    if (!branchUid) return;
    if (!versions || versions.length === 0) return;
    if (selectedVersion?.branchUid === branchUid) return;

    const rememberedId = readVersionIdForBranch(branchUid);
    const next = versions.find((v) => v.id === rememberedId) ?? versions[0];
    setSelectedVersion(next);
  }, [branchUid, versions, selectedVersion, setSelectedVersion]);
}

export function WorkflowLayout() {
  const { branchUid } = useParams();
  const { data: versions, error: versionsError } = useBranchVersions(branchUid);
  useSelectedVersionForCurrentBranch();

  // 존재하지 않는 매장 UID 로 접근한 경우 — 404 페이지로 보낸다
  const is404Error = versionsError && (versionsError as any).response?.status === 404;
  if (is404Error) {
    return <Navigate to="/404" replace />;
  }

  // 권한 없는 매장 링크(주로 공유받은 링크)로 들어온 경우 — 무슨 상황인지 알려준다.
  // 이전에는 매장 하위 API 에 권한 검사가 없어 남의 고객사 매장이 그냥 열렸고,
  // 상단 셀렉터만 빈 채로 남아 사용자가 원인을 알 수 없었다.
  if (isBranchForbiddenError(versionsError)) {
    return <BranchForbiddenPage />;
  }

  // BR-001: 버전 0개 매장은 워크플로 진입 불가 — 로딩 완료 후 빈 게 확인되면 매장목록으로 돌려보냄.
  // BranchTable / BranchSelector / 직접 URL 등 모든 진입 경로를 한 곳에서 막는 통합 가드.
  // versions 가 undefined 이면 아직 로딩 중이므로 리다이렉트하지 않음(정상 매장 깜빡임 방지).
  if (branchUid && versions !== undefined && versions.length === 0) {
    return <Navigate to="/" replace />;
  }

  return (
    <Container>
      <GNB />
      <WorkflowBar />
      <Main>
        <Outlet />
      </Main>
    </Container>
  );
}
